import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Layout/Header';
import MobileSidebar from '../components/Layout/mobileSidebar';
import { useAuth } from '../context/AuthContext';
import { pollsService, CreatePollPayload, UpdatePollPayload } from '../services/polls.service';
import { usersService } from '../services/users.service';
import { Poll } from '../types';
import {
  canCreatePolls,
  canEndPolls,
  canManagePoll,
  isElevatedFeedRole,
  normalizeRole,
} from '../utils/roles';
import { mergeBranchOptions } from '../constants/branches';
import {
  DEPARTMENT_ONLY_OPTIONS,
  matchDepartmentOnlyOption,
} from '../constants/departmentOptions';
import Combobox from '../components/Combobox';
import './Polls.css';

const MOBILE_TABLET_BREAKPOINT = 1024;

/** UI scope — mirrors Create Post; “department only” maps to API `DEPARTMENT` + creator branch. */
type PollScopeUi = 'COMPANY' | 'BRANCH' | 'DEPARTMENT_ONLY';

const Polls: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [polls, setPolls] = useState<Poll[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(
    () =>
      typeof window !== 'undefined'
        ? window.innerWidth <= MOBILE_TABLET_BREAKPOINT
        : true,
  );

  const [showCreate, setShowCreate] = useState(true);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [choiceInputs, setChoiceInputs] = useState<string[]>(['', '']);
  const [accessScope, setAccessScope] = useState<PollScopeUi>('COMPANY');
  const [branch, setBranch] = useState('');
  const [targetDepartment, setTargetDepartment] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [branchOptions, setBranchOptions] = useState<string[]>([]);
  const [metaLoading, setMetaLoading] = useState(false);

  const [editingPoll, setEditingPoll] = useState<Poll | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editChoiceInputs, setEditChoiceInputs] = useState<string[]>(['', '']);
  const [editAccessScope, setEditAccessScope] = useState<PollScopeUi>('COMPANY');
  const [editBranch, setEditBranch] = useState('');
  const [editTargetDepartment, setEditTargetDepartment] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const [voteBusy, setVoteBusy] = useState<string | null>(null);
  const [endBusy, setEndBusy] = useState<string | null>(null);
  const [selectedOption, setSelectedOption] = useState<Record<string, number>>({});
  const [pendingEndPoll, setPendingEndPoll] = useState<Poll | null>(null);
  const [pendingDeletePoll, setPendingDeletePoll] = useState<Poll | null>(null);
  const [deleteBusy, setDeleteBusy] = useState<string | null>(null);

  const r = normalizeRole(user?.role);
  const isBranchAdmin = r === 'BRANCH_ADMIN';
  const isDeptAdmin = r === 'DEPARTMENT_ADMIN';
  const needsElevatedScope = isElevatedFeedRole(user?.role) && !isBranchAdmin && !isDeptAdmin;
  const canCreate = canCreatePolls(user?.role);
  const canEnd = canEndPolls(user?.role);

  const load = useCallback(async () => {
    setError('');
    try {
      const data = await pollsService.getAll();
      setPolls(data);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load polls');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onResize = () => setIsMobileOrTablet(window.innerWidth <= MOBILE_TABLET_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (user) {
      setBranch(user.branch || '');
      setTargetDepartment(user.department || '');
    }
  }, [user]);

  useEffect(() => {
    if ((!showCreate && !editingPoll) || !needsElevatedScope) return;
    let cancelled = false;
    setMetaLoading(true);
    usersService
      .getBranches()
      .then((list) => {
        if (!cancelled) setBranchOptions(mergeBranchOptions(list));
      })
      .catch(() => {
        if (!cancelled) setBranchOptions(mergeBranchOptions([]));
      })
      .finally(() => {
        if (!cancelled) setMetaLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showCreate, editingPoll, needsElevatedScope]);

  const beginEditPoll = useCallback((poll: Poll) => {
    setShowCreate(false);
    setCreateError('');
    setEditingPoll(poll);
    setEditTitle(poll.title);
    setEditDescription(poll.description || '');
    setEditChoiceInputs(poll.choices.length ? [...poll.choices] : ['', '']);
    setEditAccessScope(
      poll.accessScope === 'DEPARTMENT' ? 'DEPARTMENT_ONLY' : poll.accessScope,
    );
    setEditBranch(poll.branch || '');
    setEditTargetDepartment(
      poll.accessScope === 'DEPARTMENT'
        ? matchDepartmentOnlyOption(poll.department ?? undefined) ||
            (poll.department || '').trim()
        : '',
    );
    setEditError('');
  }, []);

  const cancelEditPoll = useCallback(() => {
    setEditingPoll(null);
    setEditError('');
  }, []);

  useEffect(() => {
    const state = location.state as { editPollId?: string } | null;
    const id = state?.editPollId;
    if (!id || loading) return;
    const p = polls.find((x) => x.id === id);
    navigate('.', { replace: true, state: {} });
    if (p) beginEditPoll(p);
  }, [location.state, loading, polls, navigate, beginEditPoll]);

  const resetCreateForm = () => {
    setTitle('');
    setDescription('');
    setChoiceInputs(['', '']);
    setAccessScope('COMPANY');
    setBranch(user?.branch || '');
    setTargetDepartment(user?.department || '');
    setCreateError('');
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError('');
    const choices = choiceInputs.map((c) => c.trim()).filter(Boolean);
    if (!title.trim()) {
      setCreateError('Title is required');
      return;
    }
    if (choices.length < 2) {
      setCreateError('Add at least two choices');
      return;
    }

    const payload: CreatePollPayload = {
      title: title.trim(),
      description: description.trim() || undefined,
      choices,
    };

    if (needsElevatedScope) {
      if (accessScope === 'COMPANY') {
        payload.accessScope = 'COMPANY';
      } else if (accessScope === 'BRANCH') {
        if (!branch.trim()) {
          setCreateError('Branch is required');
          return;
        }
        payload.accessScope = 'BRANCH';
        payload.branch = branch.trim();
      } else if (accessScope === 'DEPARTMENT_ONLY') {
        if (!targetDepartment.trim()) {
          setCreateError('Target department is required');
          return;
        }
        // Visible to the chosen department in every branch.
        payload.accessScope = 'DEPARTMENT';
        payload.department = targetDepartment.trim();
      }
    }

    setCreating(true);
    try {
      await pollsService.create(payload);
      resetCreateForm();
      setShowCreate(true);
      await load();
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      setCreateError(Array.isArray(msg) ? msg.join(' ') : msg || 'Failed to create poll');
    } finally {
      setCreating(false);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPoll) return;
    setEditError('');
    const hasVotes = (editingPoll.voteCount ?? 0) > 0;

    if (!editTitle.trim()) {
      setEditError('Title is required');
      return;
    }

    const payload: UpdatePollPayload = {};

    if (hasVotes) {
      payload.title = editTitle.trim();
      payload.description = editDescription.trim() || undefined;
    } else {
      const choices = editChoiceInputs.map((c) => c.trim()).filter(Boolean);
      if (choices.length < 2) {
        setEditError('Add at least two choices');
        return;
      }
      payload.title = editTitle.trim();
      payload.description = editDescription.trim() || undefined;
      payload.choices = choices;
      if (needsElevatedScope) {
        if (editAccessScope === 'COMPANY') {
          payload.accessScope = 'COMPANY';
        } else if (editAccessScope === 'BRANCH') {
          if (!editBranch.trim()) {
            setEditError('Branch is required');
            return;
          }
          payload.accessScope = 'BRANCH';
          payload.branch = editBranch.trim();
        } else if (editAccessScope === 'DEPARTMENT_ONLY') {
          if (!editTargetDepartment.trim()) {
            setEditError('Target department is required');
            return;
          }
          payload.accessScope = 'DEPARTMENT';
          payload.department = editTargetDepartment.trim();
        }
      }
    }

    setEditSaving(true);
    try {
      const updated = await pollsService.update(editingPoll.id, payload);
      setPolls((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      cancelEditPoll();
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      setEditError(Array.isArray(msg) ? msg.join(' ') : msg || 'Failed to update poll');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeletePoll = (poll: Poll) => {
    setPendingDeletePoll(poll);
  };

  const confirmDeletePoll = async () => {
    if (!pendingDeletePoll) return;
    const poll = pendingDeletePoll;
    setPendingDeletePoll(null);
    setDeleteBusy(poll.id);
    try {
      await pollsService.remove(poll.id);
      if (editingPoll?.id === poll.id) cancelEditPoll();
      await load();
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      alert(Array.isArray(msg) ? msg.join(' ') : msg || 'Could not delete poll');
    } finally {
      setDeleteBusy(null);
    }
  };

  const handleVote = async (poll: Poll) => {
    const idx = selectedOption[poll.id];
    if (idx === undefined || poll.isEnded) return;
    setVoteBusy(poll.id);
    try {
      const updated = await pollsService.vote(poll.id, idx);
      setPolls((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      alert(Array.isArray(msg) ? msg.join(' ') : msg || 'Vote failed');
    } finally {
      setVoteBusy(null);
    }
  };

  const handleEnd = async (poll: Poll) => {
    setPendingEndPoll(poll);
  };

  const confirmEndPoll = async () => {
    if (!pendingEndPoll) return;
    const poll = pendingEndPoll;
    setPendingEndPoll(null);
    setEndBusy(poll.id);
    try {
      const updated = await pollsService.endPoll(poll.id);
      setPolls((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      alert(Array.isArray(msg) ? msg.join(' ') : msg || 'Could not end poll');
    } finally {
      setEndBusy(null);
    }
  };

  useEffect(() => {
    if (!pendingEndPoll && !pendingDeletePoll && !editingPoll) return;
    const onEsc = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (pendingEndPoll) {
        setPendingEndPoll(null);
        return;
      }
      if (pendingDeletePoll) {
        setPendingDeletePoll(null);
        return;
      }
      if (editingPoll && !editSaving) {
        cancelEditPoll();
      }
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [pendingEndPoll, pendingDeletePoll, editingPoll, editSaving, cancelEditPoll]);

  const scopeLabel = (p: Poll) => {
    if (p.accessScope === 'COMPANY') return 'Company-wide';
    if (p.accessScope === 'BRANCH') return `Branch: ${p.branch || '—'}`;
    return `Dept: ${p.department || '—'} (All branches)`;
  };

  return (
    <div className="polls-page">
      <Header />
      {isMobileOrTablet && (
        <button
          type="button"
          className="polls-hamburger"
          aria-label="Open menu"
          onClick={() => setMobileOpen(true)}
        >
          <span />
          <span />
          <span />-
        </button>
      )}
      {isMobileOrTablet && mobileOpen && (
        <MobileSidebar onClose={() => setMobileOpen(false)} />
      )}

      <main className="polls-main">
        <header className="polls-hero">
          <div className="polls-toolbar">
            <button type="button" className="polls-back" onClick={() => navigate('/')}>
              ← Home
            </button>
            {/* {canCreate && (
              <button
                type="button"
                className="polls-btn-primary"
                onClick={() => {
                  setShowCreate((v) => {
                    const next = !v;
                    if (next) cancelEditPoll();
                    return next;
                  });
                  setCreateError('');
                }}
              >
                {showCreate ? 'Close form' : 'Create poll'}
              </button>
            )} */}
          </div>
          <div className="polls-hero-text">
            <span className="polls-hero-icon" aria-hidden>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M4 12h4l2-7 4 14 2-7h4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <div>
              <h1 className="polls-title">Polls</h1>
            </div>
          </div>
        </header>

        {showCreate && canCreate && (
          <form className="polls-create-card" onSubmit={handleCreate}>
            <div className="polls-create-head">
              <h2>New poll</h2>
            </div>
            {createError && <div className="polls-form-error">{createError}</div>}
            <label>
              Title *
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={300}
                required
              />
            </label>
            <label>
              Description
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Optional context for voters"
              />
            </label>

            <div className="polls-choices-editor">
              <span className="polls-label">Choices * (min 2)</span>
              {choiceInputs.map((c, i) => (
                <div key={i} className="polls-choice-row">
                  <input
                    value={c}
                    onChange={(e) => {
                      const next = [...choiceInputs];
                      next[i] = e.target.value;
                      setChoiceInputs(next);
                    }}
                    placeholder={`Option ${i + 1}`}
                  />
                  {choiceInputs.length > 2 && (
                    <button
                      type="button"
                      className="polls-btn-ghost"
                      onClick={() => setChoiceInputs(choiceInputs.filter((_, j) => j !== i))}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="polls-btn-ghost"
                onClick={() => setChoiceInputs([...choiceInputs, ''])}
              >
                + Add choice
              </button>
            </div>

            {needsElevatedScope && (
              <div className="polls-scope-block">
                <div className="polls-field">
                  <span className="polls-field-label">Access</span>
                  <Combobox
                    ariaLabel="Access scope"
                    value={accessScope}
                    onChange={(v) => {
                      const next = v as PollScopeUi;
                      setAccessScope(next);
                      if (next === 'COMPANY') {
                        setBranch('');
                        setTargetDepartment('');
                      }
                      if (next === 'BRANCH') {
                        setTargetDepartment('');
                      }
                      if (next === 'DEPARTMENT_ONLY') {
                        const fromProfile = matchDepartmentOnlyOption(user?.department);
                        if (fromProfile) setTargetDepartment(fromProfile);
                      }
                    }}
                    options={[
                      { value: 'COMPANY', label: 'Home feed (all users)', hint: 'Company-wide' },
                      { value: 'BRANCH', label: 'One branch only', hint: 'Branch feed' },
                      { value: 'DEPARTMENT_ONLY', label: 'One department only', hint: 'Dept feed' },
                    ]}
                  />
                </div>
                {accessScope === 'COMPANY' && (
                  <p className="polls-field-hint">
                    Visible on the main Home feed for every user in the company.
                  </p>
                )}
                {accessScope === 'BRANCH' && (
                  <div className="polls-field">
                    <span className="polls-field-label">Branch *</span>
                    <Combobox
                      ariaLabel="Branch"
                      value={branch}
                      onChange={setBranch}
                      required
                      disabled={metaLoading && branchOptions.length === 0}
                      placeholder={metaLoading ? 'Loading branches…' : 'Select branch…'}
                      options={branchOptions.map((b) => ({ value: b, label: b }))}
                    />
                    {!metaLoading && branchOptions.length === 0 && (
                      <span className="polls-field-hint">
                        No branches found in user directory yet.
                      </span>
                    )}
                  </div>
                )}
                {accessScope === 'DEPARTMENT_ONLY' && (
                  <div className="polls-field">
                    <span className="polls-field-label">Department *</span>
                    <Combobox
                      ariaLabel="Department"
                      value={targetDepartment}
                      onChange={setTargetDepartment}
                      required
                      placeholder="Select department…"
                      options={DEPARTMENT_ONLY_OPTIONS.map((d) => ({ value: d, label: d }))}
                    />
                    <p className="polls-field-hint">
                      Uses your profile branch ({user?.branch?.trim() || '—'}) for targeting — same as
                      Create Post.
                    </p>
                  </div>
                )}
              </div>
            )}

            {(isBranchAdmin || isDeptAdmin) && (
              <div className="polls-locked-scope" aria-readonly>
                <p className="polls-locked-title">Poll audience (your account)</p>
                {isBranchAdmin && (
                  <p>
                    <strong>Branch:</strong> {user?.branch?.trim() || '—'}
                    <span className="polls-locked-hint"> — fixed; not editable</span>
                  </p>
                )}
                {isDeptAdmin && (
                  <>
                    <p>
                      <strong>Branch:</strong> {user?.branch?.trim() || '—'}
                    </p>
                    <p>
                      <strong>Department:</strong> {user?.department?.trim() || '—'}
                      <span className="polls-locked-hint"> — fixed; not editable</span>
                    </p>
                  </>
                )}
                <p className="polls-scope-note polls-locked-summary">
                  {isBranchAdmin && 'Visible to everyone in your branch feed.'}
                  {isDeptAdmin && 'Visible only in your department feed.'}
                </p>
              </div>
            )}

            <div className="polls-form-actions">
              <button type="submit" className="polls-btn-primary" disabled={creating}>
                {creating ? 'Creating…' : 'Publish poll'}
              </button>
            </div>
          </form>
        )}

        {editingPoll &&
          typeof document !== 'undefined' &&
          createPortal(
            <div
              className="polls-edit-modal-overlay"
              role="dialog"
              aria-modal="true"
              aria-labelledby="polls-edit-modal-title"
            >
              <div
                className="polls-edit-modal-shell"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <form className="polls-create-card polls-edit-card polls-edit-modal-form" onSubmit={handleEditSubmit}>
            <div className="polls-create-head">
              <h2 id="polls-edit-modal-title">Edit poll</h2>
              <button
                type="button"
                className="polls-btn-ghost"
                onClick={cancelEditPoll}
                disabled={editSaving}
              >
                Cancel
              </button>
            </div>
            {editError && <div className="polls-form-error">{editError}</div>}
            {(editingPoll.voteCount ?? 0) > 0 && (
              <p className="polls-form-note">
                This poll already has votes — only the title and description can be changed.
              </p>
            )}
            <label>
              Title *
              <input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                maxLength={300}
                required
              />
            </label>
            <label>
              Description
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                rows={3}
                placeholder="Optional context for voters"
              />
            </label>

            {(editingPoll.voteCount ?? 0) > 0 ? (
              <div className="polls-choices-readonly" aria-label="Fixed choices">
                <span className="polls-label">Choices</span>
                <ol>
                  {editingPoll.choices.map((label, i) => (
                    <li key={i}>{label}</li>
                  ))}
                </ol>
              </div>
            ) : (
              <>
                <div className="polls-choices-editor">
                  <span className="polls-label">Choices * (min 2)</span>
                  {editChoiceInputs.map((c, i) => (
                    <div key={i} className="polls-choice-row">
                      <input
                        value={c}
                        onChange={(e) => {
                          const next = [...editChoiceInputs];
                          next[i] = e.target.value;
                          setEditChoiceInputs(next);
                        }}
                        placeholder={`Option ${i + 1}`}
                      />
                      {editChoiceInputs.length > 2 && (
                        <button
                          type="button"
                          className="polls-btn-ghost"
                          onClick={() =>
                            setEditChoiceInputs(editChoiceInputs.filter((_, j) => j !== i))
                          }
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    className="polls-btn-ghost"
                    onClick={() => setEditChoiceInputs([...editChoiceInputs, ''])}
                  >
                    + Add choice
                  </button>
                </div>

                {needsElevatedScope && (
                  <div className="polls-scope-block">
                    <div className="polls-field">
                      <span className="polls-field-label">Access</span>
                      <Combobox
                        ariaLabel="Access scope"
                        value={editAccessScope}
                        onChange={(v) => {
                          const next = v as PollScopeUi;
                          setEditAccessScope(next);
                          if (next === 'COMPANY') {
                            setEditBranch('');
                            setEditTargetDepartment('');
                          }
                          if (next === 'BRANCH') {
                            setEditTargetDepartment('');
                          }
                          if (next === 'DEPARTMENT_ONLY') {
                            const fromProfile = matchDepartmentOnlyOption(user?.department);
                            if (fromProfile) setEditTargetDepartment(fromProfile);
                          }
                        }}
                        options={[
                          { value: 'COMPANY', label: 'Home feed (all users)', hint: 'Company-wide' },
                          { value: 'BRANCH', label: 'One branch only', hint: 'Branch feed' },
                          { value: 'DEPARTMENT_ONLY', label: 'One department only', hint: 'Dept feed' },
                        ]}
                      />
                    </div>
                    {editAccessScope === 'COMPANY' && (
                      <p className="polls-field-hint">
                        Visible on the main Home feed for every user in the company.
                      </p>
                    )}
                    {editAccessScope === 'BRANCH' && (
                      <div className="polls-field">
                        <span className="polls-field-label">Branch *</span>
                        <Combobox
                          ariaLabel="Branch"
                          value={editBranch}
                          onChange={setEditBranch}
                          required
                          disabled={metaLoading && branchOptions.length === 0}
                          placeholder={metaLoading ? 'Loading branches…' : 'Select branch…'}
                          options={branchOptions.map((b) => ({ value: b, label: b }))}
                        />
                      </div>
                    )}
                    {editAccessScope === 'DEPARTMENT_ONLY' && (
                      <div className="polls-field">
                        <span className="polls-field-label">Department *</span>
                        <Combobox
                          ariaLabel="Department"
                          value={editTargetDepartment}
                          onChange={setEditTargetDepartment}
                          required
                          placeholder="Select department…"
                          options={DEPARTMENT_ONLY_OPTIONS.map((d) => ({ value: d, label: d }))}
                        />
                        <p className="polls-field-hint">
                          Uses your profile branch ({user?.branch?.trim() || editingPoll.branch || '—'}) for
                          targeting — same as Create Post.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {(isBranchAdmin || isDeptAdmin) && (
                  <div className="polls-locked-scope" aria-readonly>
                    <p className="polls-locked-title">Poll audience (your account)</p>
                    {isBranchAdmin && (
                      <p>
                        <strong>Branch:</strong> {user?.branch?.trim() || '—'}
                        <span className="polls-locked-hint"> — fixed; not editable</span>
                      </p>
                    )}
                    {isDeptAdmin && (
                      <>
                        <p>
                          <strong>Branch:</strong> {user?.branch?.trim() || '—'}
                        </p>
                        <p>
                          <strong>Department:</strong> {user?.department?.trim() || '—'}
                          <span className="polls-locked-hint"> — fixed; not editable</span>
                        </p>
                      </>
                    )}
                    <p className="polls-scope-note polls-locked-summary">
                      {isBranchAdmin && 'Visible to everyone in your branch feed.'}
                      {isDeptAdmin && 'Visible only in your department feed.'}
                    </p>
                  </div>
                )}
              </>
            )}

            <div className="polls-form-actions">
              <button type="submit" className="polls-btn-primary" disabled={editSaving}>
                {editSaving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
                </form>
              </div>
            </div>,
            document.body,
          )}

        {error && <div className="polls-banner-error">{error}</div>}
        {loading ? (
          <p className="polls-muted">Loading polls…</p>
        ) : polls.length === 0 ? (
          <p className="polls-muted">No polls yet.</p>
        ) : (
          <ul className="polls-list">
            {polls.map((poll) => (
              <li key={poll.id} className="polls-card">
                <div className="polls-card-accent" aria-hidden />
                <div className="polls-card-head">
                  <h2>{poll.title}</h2>
                  <div className="polls-card-head-aside">
                    <span
                      className={`polls-scope-pill polls-scope-pill--${(poll.accessScope || 'COMPANY').toLowerCase()}`}
                    >
                      {scopeLabel(poll)}
                    </span>
                    {canManagePoll(poll, user?.id, user?.role) && (
                      <div className="polls-card-manage">
                        {!poll.isEnded && (
                          <button
                            type="button"
                            className="polls-btn-ghost polls-btn-compact"
                            onClick={() => beginEditPoll(poll)}
                          >
                            Edit
                          </button>
                        )}
                        <button
                          type="button"
                          className="polls-btn-danger polls-btn-compact"
                          onClick={() => handleDeletePoll(poll)}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                {poll.description && <p className="polls-desc">{poll.description}</p>}

                {poll.results && poll.isEnded && (
                  <div className="polls-results">
                    <div className="polls-results-head">
                      <h3>Results</h3>
                      <span className="polls-total-votes">{poll.results.total} votes</span>
                    </div>
                    {poll.choices.map((label, i) => {
                      const count = poll.results!.counts[i] ?? 0;
                      const pct = poll.results!.total
                        ? Math.round((count / poll.results!.total) * 1000) / 10
                        : 0;
                      return (
                        <div key={i} className="polls-result-row">
                          <div className="polls-result-label">
                            <span className="polls-result-num">{i + 1}</span>
                            <span>{label}</span>
                            <span className="polls-result-pct">
                              <strong>{pct}%</strong>
                              <span className="polls-result-n">{count}</span>
                            </span>
                          </div>
                          <div className="polls-bar-track">
                            <div className="polls-bar-fill" style={{ width: `${Math.max(pct, 2)}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {!poll.isEnded && poll.myVote === null && (
                  <div className="polls-vote-block">
                    <p className="polls-vote-label">Choose one option</p>
                    {poll.choices.map((label, i) => (
                      <label
                        key={i}
                        className={`polls-option-row${
                          selectedOption[poll.id] === i ? ' polls-option-row--on' : ''
                        }`}
                      >
                        <input
                          type="radio"
                          name={`poll-${poll.id}`}
                          checked={selectedOption[poll.id] === i}
                          onChange={() =>
                            setSelectedOption((prev) => ({ ...prev, [poll.id]: i }))
                          }
                        />
                        <span className="polls-option-idx">{i + 1}</span>
                        <span className="polls-option-txt">{label}</span>
                      </label>
                    ))}
                    <button
                      type="button"
                      className="polls-btn-primary polls-vote-btn"
                      disabled={selectedOption[poll.id] === undefined || voteBusy === poll.id}
                      onClick={() => handleVote(poll)}
                    >
                      {voteBusy === poll.id ? 'Submitting…' : 'Submit vote'}
                    </button>
                  </div>
                )}

                {!poll.isEnded && poll.myVote !== null && (
                  <div className="polls-voted-msg">
                    <span className="polls-voted-check" aria-hidden>
                      ✓
                    </span>
                    <p>
                      You voted for <strong>{poll.choices[poll.myVote]}</strong>. Results appear after an admin ends
                      the poll.
                    </p>
                  </div>
                )}

                {canEnd && !poll.isEnded && (
                  <button
                    type="button"
                    className="polls-btn-end"
                    disabled={endBusy === poll.id}
                    onClick={() => handleEnd(poll)}
                  >
                    {endBusy === poll.id ? 'Ending…' : 'End poll & show results'}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </main>

      {pendingEndPoll && (
        <div
          className="polls-confirm-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="polls-end-confirm-title"
          onClick={() => setPendingEndPoll(null)}
        >
          <div className="polls-confirm-card" onClick={(e) => e.stopPropagation()}>
            <h3 id="polls-end-confirm-title">End poll and publish results?</h3>
            <p>
              Everyone will immediately see vote totals for
              <strong> "{pendingEndPoll.title}"</strong>.
            </p>
            <div className="polls-confirm-actions">
              <button
                type="button"
                className="polls-btn-ghost polls-confirm-cancel"
                onClick={() => setPendingEndPoll(null)}
              >
                Cancel
              </button>
              <button type="button" className="polls-btn-primary" onClick={confirmEndPoll}>
                End poll
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingDeletePoll && (
        <div
          className="polls-confirm-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="polls-delete-confirm-title"
          onClick={() => setPendingDeletePoll(null)}
        >
          <div className="polls-confirm-card" onClick={(e) => e.stopPropagation()}>
            <h3 id="polls-delete-confirm-title">Delete poll?</h3>
            <p>
              {`Delete this poll: "${pendingDeletePoll.title}"? All votes will be removed. This cannot be undone.`}
            </p>
            <div className="polls-confirm-actions">
              <button
                type="button"
                className="polls-btn-ghost polls-confirm-cancel"
                onClick={() => setPendingDeletePoll(null)}
                disabled={deleteBusy === pendingDeletePoll.id}
              >
                Cancel
              </button>
              <button
                type="button"
                className="polls-btn-danger"
                onClick={confirmDeletePoll}
                disabled={deleteBusy === pendingDeletePoll.id}
              >
                {deleteBusy === pendingDeletePoll.id ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Polls;
